import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import CleanLayout from '../../../components/common/layout/CleanLayout';
import Message from '../../../components/common/ui/Message';
import LoginForm, {
  ILoginFormProps,
  ILoginFormValues,
} from '../../../components/login/LoginForm';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PageJSONLDSchema } from '../../../models/app.models';
import {
  IBasePage,
  ICustomerReviewsMeta,
  ILoginPage,
  IMe,
  IMetaComponent,
  ServiceType,
} from '../../../models/strapi.models';
import { fetchStrapi } from '../../../services/api.service';
import { logIn } from '../../../services/auth.service';
import { logError, logInfo } from '../../../services/log.service';
import { storeData } from '../../../services/storage.service';
import { buildApiKey, getCommonTranslations } from '../../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../../shared/useJSONLDSchema';
import {
  clearPostLoginRedirect,
  getPostLoginRedirect,
} from '../../../shared/usePostLoginRedirect';
import { getTranslation } from '../../../shared/useUtils';
import {
  IAppPage,
  IAppPageLocalization,
  setCurrentCurrency,
  setCurrentLocale,
  setIsLoading,
} from '../../../store/slices/appSlice';
import { setIsAuthenticated, setUser } from '../../../store/slices/authSlice';
import { NextPageWithLayout } from '../../_app';

interface ILoginPageProps extends IBasePage {
  meta: IMetaComponent;
  labels: {
    emailLabel: string;
    passwordLabel: string;
    forgotPasswordButton: string;
    logInButton: string;
    registerTip: string;
    registerButton: string;
    formSubmitErrorMessage: string;
    formValidation: {
      requiredField: string;
      emailFormatNotValid: string;
    };
    alternativeLoginMethodsTitle: string;
  };
}

interface ILoginPageState {
  submitResultType: 'success' | 'error' | undefined;
}

const LoginPage: NextPageWithLayout<ILoginPageProps> = (
  props: ILoginPageProps
) => {
  const appState = useAppSelector((state) => state.app);
  const initialPageState: ILoginPageState = {
    submitResultType: undefined,
  };
  const [state, setState] = useState(initialPageState);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const currentOrderState = useAppSelector((state) => state.currentOrder);

  const getRoute = useCallback(
    (actualPath: string, language?: string, slug?: string) => {
      let localizedRoute = actualPath;

      if (slug) {
        actualPath = actualPath.replace('[slug]', slug);

        if (language !== 'en') {
          actualPath = `/${language}${actualPath}`;
        }
      }

      if (language && slug && actualPath.includes('/blog/')) {
        // Here, the slug is the blog post id
        const page = appState.pages?.find((x: IAppPage) => {
          return x.destination.includes(slug);
        });

        if (page) {
          // Get its alternative page (localization) based on language
          const altPage = page?.localizations?.find(
            (x: IAppPageLocalization) => x.locale === language
          );

          if (altPage) {
            // Look for the localized page
            const localizedPage = appState.pages?.find((x: IAppPage) => {
              return x.destination.includes(altPage?.id.toString());
            });

            if (localizedPage) {
              localizedRoute = localizedPage.source;
            }
          }
        }
      } else {
        const localizedPage = appState.pages?.find(
          (x: IAppPage) =>
            x &&
            x.destination === actualPath &&
            (language ? x.language === language : x.language === router.locale)
        );

        if (localizedPage) {
          localizedRoute = localizedPage.source;
        }
      }

      logInfo(
        [{ actualPath, language, slug, localizedRoute }],
        'Login → Localized route'
      );

      return localizedRoute;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appState.pages, router.locale]
  );

  const userLogin = useCallback(
    async (email: string, password: string) => {
      dispatch(setIsLoading(true));

      try {
        const me: IMe = await logIn(email, password);
        const userLocale: string = me.user_info?.language?.twoLetterIso || 'en';
        const userCurrencyCode = me.user_info?.currency?.isoCode || 'USD';

        storeData({ code: userCurrencyCode }, 'user-currency');
        storeData({ code: userLocale }, 'user-locale');
        dispatch(
          setCurrentCurrency(
            appState.currencies.find((x) => x.isoCode === userCurrencyCode)!
          )
        );
        dispatch(setCurrentLocale(userLocale));
        dispatch(setIsAuthenticated(me !== undefined));
        dispatch(setUser(me));

        logInfo(['User logged in', me], 'Login page → userLogin');

        let redirectUrl;

        if (currentOrderState.type === ServiceType.APPLICATION) {
          // Redirect if there is an ongoing order.
          redirectUrl = `${getRoute('/trademark-registration', userLocale)}`;
          storeData(2, 'checkout-step');
        } else if (currentOrderState.type === ServiceType.SEARCH) {
          // Redirect if there is an ongoing order.
          redirectUrl = `${getRoute('/trademark-search', userLocale)}`;
          storeData(2, 'checkout-step');
        } else {
          // Check sessionStorage or document.referrer for post-login redirect
          const savedRedirect = getPostLoginRedirect();
          if (savedRedirect) {
            redirectUrl = savedRedirect;
            clearPostLoginRedirect();
          } else {
            // Fallback → Navigate to Account → Profile
            redirectUrl = getRoute('/account/profile', userLocale);
          }
        }

        if (redirectUrl) {
          logInfo(
            ['Redirect to URL after login', redirectUrl],
            'Login page → userLogin'
          );
          window.location.href = `${window.location.origin}${redirectUrl}`;
        }
      } catch (error) {
        logError(['User not logged in', error], 'Login page → userLogin');
        setState((state) => ({ ...state, submitResultType: 'error' }));
        dispatch(setIsLoading(false));
      }
    },
    [appState.currencies, currentOrderState.type, dispatch, getRoute]
  );

  const oAuthLogin = useCallback(
    async (provider: 'github' | 'linkedin' | 'microsoft' | 'google') => {
      dispatch(setIsLoading(true));
      setTimeout(() => {
        router.push(
          `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/connect/${provider}`
        );
      }, 250);
    },
    [dispatch, router]
  );

  const loginFormProps: ILoginFormProps = useMemo<ILoginFormProps>(
    () => ({
      labels: {
        emailLabel: props.labels.emailLabel,
        passwordLabel: props.labels.passwordLabel,
        submitButtonLabel: props.labels.logInButton,
        forgotButtonLabel: props.labels.forgotPasswordButton,
        registerLabel: props.labels.registerTip,
        registerButtonLabel: props.labels.registerButton,
        alternativeLoginMethodsTitle: props.labels.alternativeLoginMethodsTitle,
      },
      validation: {
        emailNotValidMessage: props.labels.formValidation.emailFormatNotValid,
        emailRequiredMessage: props.labels.formValidation.requiredField,
        passwordRequiredMessage: props.labels.formValidation.requiredField,
      },
      onForgotPasswordClick: () => {
        router.push('/account/forgot-password');
      },
      onRegisterClick: () => {
        const route = `/account/register`;
        router.push(route);
      },
      onFormSubmit: async (data: ILoginFormValues) =>
        await userLogin(data.email as string, data.password as string),
      onOAuthLogin: oAuthLogin,
    }),
    [
      oAuthLogin,
      props.labels.alternativeLoginMethodsTitle,
      props.labels.emailLabel,
      props.labels.forgotPasswordButton,
      props.labels.formValidation.emailFormatNotValid,
      props.labels.formValidation.requiredField,
      props.labels.logInButton,
      props.labels.passwordLabel,
      props.labels.registerButton,
      props.labels.registerTip,
      router,
      userLogin,
    ]
  );

  // No longer needed - we use sessionStorage and document.referrer instead
  // This improves SEO by eliminating URL variants with redirectUrl query params

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="robots" content="noindex, nofollow" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <LoginForm {...loginFormProps} />
        </div>
      </div>
      <div className="page-content-section is-narrow">
        <div className="page-content-block">
          {state.submitResultType === 'error' && (
            <Message
              type="danger"
              iconPath={'/assets/images/icons/AlertIcon.svg'}>
              <ReactMarkdown>
                {props.labels.formSubmitErrorMessage as string}
              </ReactMarkdown>
            </Message>
          )}
        </div>
      </div>
    </div>
  );
};

LoginPage.getLayout = function getLayout(
  page: ReactElement,
  pageProps: ILoginPageProps
) {
  return <CleanLayout title={pageProps.meta.title}>{page}</CleanLayout>;
};

export async function getStaticProps(
  context: any
): Promise<{ props: ILoginPageProps }> {
  const pageUrlParams = new URLSearchParams({
    populate: '*',
  });

  const page = (
    await fetchStrapi<any>(`/login-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ILoginPage;
  const commonTranslations = await getCommonTranslations();

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const props: ILoginPageProps = {
    pageSchema,
    meta: getTranslation(page.meta, context.locale),
    labels: {
      emailLabel: getTranslation(page.emailLabel, context.locale)
        .text as string,
      forgotPasswordButton: getTranslation(
        page.forgotPasswordButton,
        context.locale
      ).text as string,
      formSubmitErrorMessage: getTranslation(
        page.formSubmitErrorMessage,
        context.locale
      ).text as string,
      logInButton: getTranslation(page.logInButton, context.locale)
        .text as string,
      passwordLabel: getTranslation(page.passwordLabel, context.locale)
        .text as string,
      registerButton: getTranslation(page.registerButton, context.locale)
        .text as string,
      registerTip: getTranslation(page.registerTip, context.locale)
        .text as string,
      formValidation: {
        requiredField: getTranslation(
          commonTranslations.requiredField,
          context.locale
        ).text as string,
        emailFormatNotValid: getTranslation(
          commonTranslations.emailFormatNotValid,
          context.locale
        ).text as string,
      },
      alternativeLoginMethodsTitle: getTranslation(
        page.alternativeLoginMethodsTitle,
        context.locale
      ).text as string,
    },
  };

  return {
    props,
  };
}

export default LoginPage;
